import React from 'react';
import shortid from 'shortid';

type PathInfo = [string, URLSearchParams, string];

const pathInfo = (path: string | Location): PathInfo => {
  const { pathname, search, hash } =
    typeof path === 'string' ? new URL(path) : path;

  const params = new URLSearchParams(search);
  return [pathname, params, hash.replace(/^#/, '')];
};

const currentPathInfo = (): PathInfo => {
  return pathInfo(document.location);
};

export const makeUrl = (
  params: Record<string, string | undefined>,
  hash?: string
) => {
  const url = new URL(window.location.href);
  for (const key in params) {
    if (params[key] === undefined) {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, params[key]);
    }
  }
  if (hash !== undefined) {
    url.hash = hash;
  }
  return url.toString();
};

interface LinkProps<T extends {}>
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  router: Router<T>;
}
export const Link = <T extends {}>({
  to,
  router,
  children,
  ...rest
}: LinkProps<T>) => (
  <a
    href={to}
    onClick={e => {
      if (!e.metaKey) {
        router.navigate(to);
        e.preventDefault();
      }
    }}
    {...rest}
  >
    {children}
  </a>
);

const docReady = (callback: () => void) => {
  if (['complete', 'interactive'].includes(document.readyState)) {
    callback();
  } else {
    document.addEventListener('DOMContentLoaded', callback);
  }
};

interface RouterOptions {
  enabled?: () => boolean;
  replace?: boolean;
}

// TODO consider making a QueryRouter variant where you give it the params it cares about and it
// deals with updating them, ignoring others etc
class Router<T extends {}> {
  public routeInfo: T;
  private readonly handler: (event: PopStateEvent) => void;
  private readonly id: string;

  constructor(
    private readonly parsePath: (
      path: string,
      params: URLSearchParams,
      hash: string | undefined
    ) => T,
    private readonly onRouteChange?: (
      routeInfo: T,
      userInitiated: boolean
      // initial?: boolean
    ) => void,
    private readonly options?: RouterOptions
  ) {
    this.id = shortid.generate();

    // TODO consider making this observable, so we can refer to it directly when rendering
    // we don't want to mirror this value into state if we can help it
    // this.routeInfo = observable(parsePath(...currentPathInfo()));
    this.routeInfo = parsePath(...currentPathInfo());
    docReady(() => {
      if (this.enabled()) {
        this.onRouteChange?.(this.routeInfo, false);
      }
    });

    this.handler = (event: PopStateEvent) => {
      if (this.enabled()) {
        this.handleChange(currentPathInfo(), false);
      }
    };
    window.addEventListener('popstate', this.handler);
  }

  private enabled() {
    if (!this.options?.enabled) {
      return true;
    }
    return this.options.enabled();
  }

  public destroy() {
    window.removeEventListener('popstate', this.handler);
  }

  public navigate(path: string) {
    if (this.enabled() && path !== window.location.href) {
      // each router must only replace its own history entries, to avoid interfering
      // with other routers (e.g. turbolinks)
      if (this.options?.replace && window.history.state?.id === this.id) {
        window.history.replaceState({ id: this.id }, '', path);
      } else {
        window.history.pushState({ id: this.id }, '', path);
      }
    }
    this.handleChange(pathInfo(path), true);
  }

  private handleChange(pathInfo: PathInfo, userInitiated: boolean) {
    const routeInfo = this.parsePath(...pathInfo);
    let updated = false;

    for (const k in routeInfo) {
      if (routeInfo[k] !== this.routeInfo[k]) {
        this.routeInfo[k] = routeInfo[k];
        updated = true;
      }
    }

    if ((userInitiated || updated) && this.onRouteChange) {
      this.onRouteChange(this.routeInfo, userInitiated);
    }
  }
}

export default Router;
